import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "services/supabaseClient";
import SectionCard from "components/SectionCard";
import FooterCompact from "components/FooterCompact";
import PasswordFields from "components/PasswordFields";
import { validatePassword } from "lib/passwordPolicy";
import { callFunction } from "lib/edge";
import {
  clearSessionLogoutFlags,
  writeLastActivity,
} from "lib/sessionActivity";

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const normalizeCode = (value) => String(value || "").replace(/\s+/g, "");

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [activationRecovery, setActivationRecovery] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      setEmail(normalizeEmail(new URL(window.location.href).searchParams.get("email")));
    } catch {
      // Keep the field empty when the current location cannot be parsed.
    }
  }, []);

  const verify = async (event) => {
    event.preventDefault(); setError(""); setLoading(true);
    try {
      // Recovery verification creates a fresh session through SIGNED_IN.
      // Treat this explicit user action as the new inactivity baseline.
      clearSessionLogoutFlags();
      writeLastActivity(Date.now());
      const { data, error: verifyError } = await supabase.auth.verifyOtp({ email: normalizeEmail(email), token: normalizeCode(code), type: "recovery" });
      if (verifyError || !data?.user || !data?.session) throw verifyError || new Error("missing_session");
      setActivationRecovery(!!data.user.app_metadata?.activation_pending);
      setVerified(true);
    } catch { setError("The verification code is invalid or has expired. Please request a new code."); }
    finally { setLoading(false); }
  };

  const reset = async (event) => {
    event.preventDefault(); setError("");
    const validation = validatePassword(password, confirmation);
    if (validation) { setError(validation); return; }
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) { setError("Unable to reset the password. Please request a new code and try again."); setLoading(false); return; }
    if (activationRecovery) {
      try {
        await callFunction("invite-user", { body: { action: "complete_password" } });
        await supabase.auth.refreshSession();
        navigate("/signup-user", { replace: true });
      } catch {
        setError("Your password was saved, but account setup could not continue. Please contact your Compass administrator.");
        setLoading(false);
      }
      return;
    }
    await supabase.auth.signOut();
    setMessage("Password reset successfully. You may now log in.");
    setTimeout(() => navigate("/", { replace: true }), 1200);
  };

  return <div className="bg-app text-app bg-surface min-h-screen flex flex-col"><div className="flex-1 p-6 flex items-start justify-center"><div className="w-full max-w-lg"><h1 className="text-2xl font-semibold">Reset Password</h1><SectionCard className="mt-4">{!verified ? <><p className="mb-4 text-sm text-muted">Enter your email address and the temporary verification code shown in the password-reset email.</p><form onSubmit={verify} className="space-y-4"><div><label className="block mb-1 font-medium">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2 rounded-md border border-app input-app" required /></div><div><label className="block mb-1 font-medium">Verification code</label><input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" className="w-full px-4 py-2 rounded-md border border-app input-app" required /></div><button className="btn btn-primary w-full" disabled={loading}>{loading ? "Verifying..." : "Verify Code"}</button></form></> : <form onSubmit={reset} className="space-y-4"><PasswordFields password={password} confirmation={confirmation} onPassword={setPassword} onConfirmation={setConfirmation} disabled={loading} /><button className="btn btn-primary w-full" disabled={loading}>{loading ? "Saving..." : "Reset Password"}</button></form>}{error && <div role="alert" className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}{message && <div className="mt-4 panel-subtle p-3 rounded text-sm text-center">{message}</div>}</SectionCard></div></div><FooterCompact /></div>;
}
